<template>
  <v-main>
    <v-container>
      <v-row>
        <v-col class="text-center">
          <v-progress-circular
            v-if="isInitialLoading && orders.length === 0"
            indeterminate
          />
        </v-col>
      </v-row>

      <v-container v-if="!isInitialLoading && !orders.length">
        <h1 class="text-center">
          {{ errorMessage }}
        </h1>
      </v-container>
      <v-container v-if="!isInitialLoading || orders.length > 0">
        <v-row justify="center">
          <v-col class="grey lighten-4">
            <order-overview
              :id="id"
              class="mb-8"
            />
            <v-card
              v-for="order in sortedOrders"
              :key="`${getOrderKey(order)}-${refreshTimestamp}`"
              class="mb-1"
              elevation="3"
            >
              <order-card
                :order="order"
                :ship-to-address-info="shipToAddressInfo"
              />
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </v-container>
  </v-main>
</template>
<script lang="ts">
import { OrderNumber, SofieShipToAddress, TrackingInfoWithEta } from "@/types/types";

import { defineComponent } from "vue";
import OrderCard, { cleanupOrphanedComponentStates } from "@/components/alco/OrderCard.vue";
import OrderOverviewVue from "@/components/alco/OrderOverview.vue";

const orderPositions: Record<string, number> = {};

type Data = {
  errorMessage: string;
  isInitialLoading: boolean;
  isBackgroundRefreshing: boolean;
  id: string;
  orders: Array<TrackingInfoWithEta & { AttemptedNotDelivered: boolean }>;
  shipToAddressInfo: SofieShipToAddress;
  orderId: OrderNumber;
  refreshTimestamp: number;
};

export default defineComponent({
  name: "TrackingDetail",
  components: {
    "order-card": OrderCard,
    "order-overview": OrderOverviewVue,
  },
  data: function (): Data {
    return {
      isInitialLoading: false,
      isBackgroundRefreshing: false,
      id: "",
      shipToAddressInfo: { Address: "", City: "", State: "", Zip: "" },
      orderId: 0,
      errorMessage: "Unable to locate this tracking number.",
      orders: new Array<TrackingInfoWithEta & { AttemptedNotDelivered: boolean }>(),
      refreshTimestamp: Date.now(),
    };
  },
  computed: {
    sortedOrders() {
      return this.orders.slice().sort((a, b) => {
        const keyA = this.getOrderKey(a);
        const keyB = this.getOrderKey(b);
        const positionA = orderPositions[keyA];
        const positionB = orderPositions[keyB];

        if (positionA !== undefined && positionB !== undefined) {
          return positionA - positionB;
        }

        if (positionA !== undefined) return -1;
        if (positionB !== undefined) return 1;
        return 0;
      });
    }
  },
  mounted() {
    const { id } = this.$route.params;
    const selectedDate = this.$route.query.selectedDate as string;
    this.id = id;

    this.getTrackingInfo(id, selectedDate, true);

    setInterval(() => {
      this.getTrackingInfo(id, selectedDate, false);
    }, 300000);
  },
  methods: {
    getOrderKey(order: TrackingInfoWithEta & { AttemptedNotDelivered: boolean }): string {
      return `order-${order.OrderID}`;
    },

    getTrackingInfo: function (orderReference: string, orderDate: string, isInitialLoad = false) {
      const scrollPosition = window.pageYOffset || document.documentElement.scrollTop;

      if (isInitialLoad) {
        this.isInitialLoading = true;
        this.isBackgroundRefreshing = false;
      } else {
        this.isInitialLoading = false;
        this.isBackgroundRefreshing = true;
      }

      this.$apiClient.GET('/alco/{stopTwoRefOne}/{orderDate}', {
        params: {
          path: {
            stopTwoRefOne: orderReference,
            orderDate: orderDate
          }
        }
      }).then((response) => {
          if (response.data && response.data.length > 0) {
            const ordersToArrange = response.data;

            const ordersInProgress = ordersToArrange.filter(orderToArrange => orderToArrange.PickupDateTime && !orderToArrange.DeliveryDateTime).sort((orderA, orderB) => new Date(orderA.Eta ?? Date.now()).valueOf() - new Date(orderB.Eta ?? Date.now()).valueOf());
            const ordersNotStarted = ordersToArrange.filter(orderToArrange => !orderToArrange.PickupDateTime && !orderToArrange.DeliveryDateTime);
            const completedOrders = ordersToArrange.filter(orderToArrange => orderToArrange.PickupDateTime && orderToArrange.DeliveryDateTime);

            const newOrders = ordersInProgress.concat(completedOrders, ordersNotStarted).map(order => {
              return Object.assign({}, order);
            });

            this.orders = newOrders;

            this.manageStateDiscrepancies();

            this.orderId = this.orders[0].OrderID;
            const { Address, City, State, Zip } = this.orders[0];
            Object.assign(this.shipToAddressInfo, { Address, City, State, Zip });

            this.refreshTimestamp = Date.now();

            this.isInitialLoading = false;
            this.isBackgroundRefreshing = false;

            if (!isInitialLoad) {
              this.$nextTick(() => {
                this.$nextTick(() => {
                  window.scrollTo({ top: scrollPosition, behavior: 'instant' });
                  setTimeout(() => {
                    window.scrollTo({ top: scrollPosition, behavior: 'instant' });
                  }, 100);
                });
              });
            }
          } else {
            if (isInitialLoad) {
              this.orders = [];
            }
            this.isInitialLoading = false;
            this.isBackgroundRefreshing = false;
          }
        },
        () => {
          this.isInitialLoading = false;
          this.isBackgroundRefreshing = false;

          if (!isInitialLoad) {
            this.$nextTick(() => {
              this.$nextTick(() => {
                window.scrollTo({ top: scrollPosition, behavior: 'instant' });
                setTimeout(() => {
                  window.scrollTo({ top: scrollPosition, behavior: 'instant' });
                }, 100);
              });
            });
          }
        }
      );
    },

    getLaterDateTime(dateTimeStringOne: string, dateTimeStringTwo: string) {
      return dateTimeStringOne > dateTimeStringTwo ? dateTimeStringOne : dateTimeStringTwo;
    },

    manageStateDiscrepancies() {
      const currentOrderKeys = new Set(this.orders.map(order => this.getOrderKey(order)));
      this.cleanupOrphanedStates(currentOrderKeys);
      cleanupOrphanedComponentStates(currentOrderKeys);
      this.assignPositionsToNewOrders();
      this.compactPositions(currentOrderKeys);
    },

    cleanupOrphanedStates(currentOrderKeys: Set<string>) {
      Object.keys(orderPositions).forEach(orderKey => {
        if (!currentOrderKeys.has(orderKey)) {
          delete orderPositions[orderKey];
        }
      });
    },

    assignPositionsToNewOrders() {
      const existingPositions = Object.values(orderPositions);
      const maxPosition = existingPositions.length > 0 ? Math.max.apply(Math, existingPositions) : -1;

      let nextPosition = maxPosition + 1;

      this.orders.forEach(order => {
        const orderKey = this.getOrderKey(order);
        if (orderPositions[orderKey] === undefined) {
          orderPositions[orderKey] = nextPosition++;
        }
      });
    },

    compactPositions(currentOrderKeys: Set<string>) {
      const ordersWithPositions = Array.from(currentOrderKeys)
        .map(orderKey => ({
          orderKey,
          position: orderPositions[orderKey]
        }))
        .sort((a, b) => a.position - b.position);

      ordersWithPositions.forEach(({ orderKey }, index) => {
        orderPositions[orderKey] = index;
      });
    }
  },
});
</script>